/**
 * Admin V3 — Onboarding — Beta Status Card (READ-ONLY)
 *
 * AV3-34 / P1-B3-6. A read-only operational indicator migrated from the legacy
 * V1 Beta tab (AdminDashboard.js:1626-1724, which was display-only). Shows the
 * platform beta mode + driver cap + driver/booking counts.
 *
 * SOURCE OF TRUTH: the existing endpoint GET /admin/beta/status (RBAC
 * admin:beta:status). This component is a pure PRESENTATION of that payload —
 * it introduces NO second source of truth.
 *
 * EXPLICIT NON-CAPABILITIES (governance, AV3-34):
 *   - NO editable control / toggle / switch / action button.
 *   - NO mutation hook, no POST/PATCH/PUT/DELETE, no optimistic update.
 *   - BETA_MODE / MAX_ACTIVE_DRIVERS are env-only backend config; they are NOT
 *     read, written, or exposed as editable here. Changing them remains an
 *     env + restart + deploy operation (out of the app).
 *
 * RBAC: HIDE-gated on can('admin:beta:status'); the query is disabled when the
 * permission is absent (no 403 fetch). Backend @rbac_permission is authoritative.
 */

import React from 'react';
import { Shield, Users, Clock, CheckCircle2, Layers, Calendar, RotateCcw, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import usePermissions from '../../shared/hooks/usePermissions';
import { t, DEFAULT_LOCALE } from '../../i18n';

const BETA_STATUS_PERMISSION = 'admin:beta:status';

const StatCell = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
    </div>
    <div className="text-2xl font-bold text-slate-900">{value ?? '—'}</div>
  </div>
);

const num = (v) => (typeof v === 'number' ? v : '—');

const BetaStatusCard = ({ locale = DEFAULT_LOCALE }) => {
  const { can } = usePermissions();
  const canView = can(BETA_STATUS_PERMISSION);

  // Query disabled without the permission → no 403 fetch. READ-ONLY.
  const { data, loading, error, refetch } = useAdminQuery('/admin/beta/status', {}, { enabled: canView });

  // Fail-closed HIDE: no card when the permission cannot be confirmed.
  if (!canView) return null;

  const betaMode = Boolean(data?.beta_mode);
  const drivers = data?.drivers || {};
  const bookings = data?.bookings || {};
  const showSkeleton = loading && !data;

  const badge = data ? (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold',
        betaMode ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700',
      )}
      data-testid="adminv3-beta-mode-badge"
    >
      <Shield className="h-3.5 w-3.5" />
      {t(betaMode ? 'onboarding.beta.mode.beta' : 'onboarding.beta.mode.production', locale)}
    </span>
  ) : null;

  return (
    <SectionCard
      title={t('onboarding.beta.title', locale)}
      icon={Shield}
      iconClass={betaMode ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}
      action={badge}
    >
      {error ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="adminv3-beta-status-error">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-rose-700">{t('onboarding.beta.error', locale)}</p>
              <p className="text-xs text-rose-600 mt-1 break-words">{String(error)}</p>
            </div>
            <Button
              variant="outline" size="sm" onClick={refetch}
              className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
              data-testid="adminv3-beta-status-retry"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              {t('onboarding.beta.retry', locale)}
            </Button>
          </div>
        </div>
      ) : showSkeleton ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adminv3-beta-status-skeleton">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="h-3 w-20 rounded bg-slate-100 animate-pulse mb-3" />
              <div className="h-6 w-12 rounded bg-slate-100 animate-pulse" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adminv3-beta-status-grid">
          <StatCell icon={Users}        label={t('onboarding.beta.maxDrivers', locale)}       value={num(data?.max_active_drivers)} testId="adminv3-beta-stat-max" />
          <StatCell icon={CheckCircle2} label={t('onboarding.beta.approved', locale)}         value={num(drivers.approved)}         testId="adminv3-beta-stat-approved" />
          <StatCell icon={Clock}        label={t('onboarding.beta.pending', locale)}          value={num(drivers.pending)}          testId="adminv3-beta-stat-pending" />
          <StatCell icon={Layers}       label={t('onboarding.beta.remaining', locale)}        value={num(drivers.remaining_slots)}  testId="adminv3-beta-stat-remaining" />
          <StatCell icon={Users}        label={t('onboarding.beta.totalDrivers', locale)}     value={num(drivers.total)}            testId="adminv3-beta-stat-total-drivers" />
          <StatCell icon={Calendar}     label={t('onboarding.beta.totalBookings', locale)}    value={num(bookings.total)}           testId="adminv3-beta-stat-total-bookings" />
          <StatCell icon={CheckCircle2} label={t('onboarding.beta.completedBookings', locale)} value={num(bookings.completed)}      testId="adminv3-beta-stat-completed-bookings" />
        </div>
      )}
    </SectionCard>
  );
};

export { BETA_STATUS_PERMISSION };
export default BetaStatusCard;
